import { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, Paginator, fmtDate } from "@/lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function ActivityLogs() {
  const [page, setPage] = useState(1);
  const limit = 30;
  const { data, loading, error, refetch } = useFetch(`/admin/logs/activity?page=${page}&limit=${limit}`, [page]);
  return loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
    !data?.items?.length ? <SectionCard className="p-2"><EmptyState label="No activity logs" /></SectionCard> : (
      <>
        <SectionCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-logs-table">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr><th className="px-4 py-2.5 font-semibold">Action</th><th className="px-4 py-2.5 font-semibold">Entity</th><th className="px-4 py-2.5 font-semibold">User</th><th className="px-4 py-2.5 font-semibold">IP</th><th className="px-4 py-2.5 font-semibold">When</th></tr>
              </thead>
              <tbody className="divide-y">
                {data.items.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/30" data-testid={`activity-${l.id}`}>
                    <td className="px-4 py-2.5 font-medium">{l.action}</td>
                    <td className="px-4 py-2.5 text-xs">{l.entity}{l.entity_id ? ` · ${String(l.entity_id).slice(-6)}` : ""}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{l.user_id ? String(l.user_id).slice(-6) : "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{l.ip || "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        <Paginator page={page} total={data.total} limit={limit} onPage={setPage} />
      </>
    );
}

function SecurityLogs() {
  const [page, setPage] = useState(1);
  const limit = 30;
  const { data, loading, error, refetch } = useFetch(`/admin/logs/security?page=${page}&limit=${limit}`, [page]);
  return loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
    !data?.items?.length ? <SectionCard className="p-2"><EmptyState label="No security logs" /></SectionCard> : (
      <>
        <SectionCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="security-logs-table">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr><th className="px-4 py-2.5 font-semibold">Event</th><th className="px-4 py-2.5 font-semibold">Identifier</th><th className="px-4 py-2.5 font-semibold">Result</th><th className="px-4 py-2.5 font-semibold">IP</th><th className="px-4 py-2.5 font-semibold">When</th></tr>
              </thead>
              <tbody className="divide-y">
                {data.items.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/30" data-testid={`security-${l.id}`}>
                    <td className="px-4 py-2.5 font-medium">{l.event}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{l.identifier || "—"}</td>
                    <td className="px-4 py-2.5">
                      {l.success ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Success</span>
                        : <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><XCircle className="h-3.5 w-3.5" /> Failed</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{l.ip || "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        <Paginator page={page} total={data.total} limit={limit} onPage={setPage} />
      </>
    );
}

export default function Logs() {
  return (
    <div>
      <PageHeader title="Activity & Security Logs" subtitle="Audit trail of admin actions and login/security events" />
      <Tabs defaultValue="activity">
        <TabsList data-testid="logs-tabs">
          <TabsTrigger value="activity" data-testid="logs-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="security" data-testid="logs-tab-security">Security</TabsTrigger>
        </TabsList>
        <TabsContent value="activity" className="mt-4"><ActivityLogs /></TabsContent>
        <TabsContent value="security" className="mt-4"><SecurityLogs /></TabsContent>
      </Tabs>
    </div>
  );
}
